/* eslint-disable react/display-name */
/* eslint-disable react/require-default-props */
import { Badge, Box, Flex, Heading, IconButton, Text } from '@chakra-ui/core';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GiPlainCircle } from 'react-icons/gi';
import { IoDocumentText, IoDocumentsSharp } from 'react-icons/io5';
import { MdOutlineUpgrade } from 'react-icons/md';
import { RiHandCoinFill } from 'react-icons/ri';
import { Link } from 'react-router-dom';
import colors from '../../styles/colors';
import { useStoreState, useStoreActions } from '../../models/hooks';

interface StatCardProps {
  'data-testid'?: string;
  to?: string;
  icon: React.ReactElement;
  label: string;
  count?: number | string;
  ml?: string;
  noLink?: boolean;
  borderColor?: string;
  hoverBorderColor?: string;
}

const StatCard = React.memo(
  ({
    'data-testid': dataTestId,
    to = '',
    icon,
    label,
    count,
    ml,
    noLink = false,
    borderColor = 'blue.30',
    hoverBorderColor = colors.blue[500],
  }: StatCardProps) => {
    const content = (
      <Flex
        data-testid={dataTestId}
        py="8px"
        px="15px"
        alignItems="center"
        justifyContent="space-between"
        background={colors.white}
        borderRadius="8px"
        border="2px solid"
        borderColor={borderColor}
        ml={ml}
        cursor={noLink ? 'default' : 'pointer'}
        sx={{
          transition: 'transform 0.1s ease-in-out',
          ...(!noLink && {
            _hover: {
              borderColor: hoverBorderColor,
              boxShadow: `0 0 10px ${hoverBorderColor}`,
            },
          }),
        }}
      >
        {icon}
        <Text fontSize="15px" fontWeight="bold" color={colors.gray[600]} ml="6px">
          {label}
        </Text>
        {count !== undefined && (
          <Text
            fontSize="20px"
            color={colors.gray[900]}
            fontWeight={700}
            ml="10px"
            borderLeft="1px solid"
            borderColor="blue.30"
            pl="10px"
            lineHeight={1}
          >
            {count}
          </Text>
        )}
      </Flex>
    );

    if (noLink || !to) {
      return content;
    }

    return <Link to={to}>{content}</Link>;
  },
);

export const TodoCounts = React.memo((): JSX.Element | null => {
  const { t } = useTranslation(['toDoList', 'requisition']);
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);
  const counts = useStoreState((state) => state.todo.counts);
  const interviewCounts = useStoreState((state) => state.todo.interviewCounts);

  const openTodoSummary = useStoreState((state) => state.todo.openTodoSummary);
  const { setOpenTodoSummary } = useStoreActions((actions) => actions.todo);

  const outstandingOffersIcon = useMemo(
    () => (
      <IconButton
        isRound
        variant="solid"
        background="blue.400"
        aria-label="Done"
        fontSize="14px"
        h="24px"
        w="24px"
        minWidth="24px"
        icon={<RiHandCoinFill color={colors.white} />}
      />
    ),
    [],
  );

  const hiresProcessingIcon = useMemo(
    () => <IoDocumentText style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />,
    [],
  );

  const requisitionIcon = useMemo(
    () => <IoDocumentsSharp style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />,
    [],
  );

  const toDoIcon = useMemo(() => <GiPlainCircle style={{ marginRight: '4px' }} color={colors.blue.default} size="14px" />, []);

  if (!counts) {
    return null;
  }

  const totalCount = interviewCounts?.total ?? counts?.total;
  const totalTodo = interviewCounts?.toDo ?? counts?.toDo;

  return (
    <Box p="12px" bg={colors.white}>
      <Flex alignItems="center">
        {totalCount > 0 && (
          <Flex
            pl="10px"
            pr="10px"
            alignItems="center"
            justifyContent="space-between"
            background={colors.input.background}
            borderRadius="30px"
            cursor="pointer"
            _hover={{ background: colors.gray[200] }}
            onClick={() => setOpenTodoSummary(!openTodoSummary)}
          >
            <MdOutlineUpgrade size="30px" style={{ transform: openTodoSummary ? 'rotate(270deg)' : 'rotate(90deg)' }} />
            <Heading as="h4" fontSize="md" ml={2}>
              {t('toDoList:summary')}
            </Heading>
            <Badge
              background={totalCount === 0 ? colors.green[500] : colors.red[500]}
              w="max-content"
              minW="20px"
              lineHeight="20px"
              borderRadius={totalCount < 10 ? '50%' : '12px'}
              textAlign="center"
              color={colors.white}
              fontSize="14px"
              fontWeight={700}
              ml="6px"
              p={totalCount < 10 ? 0 : '0 4px'}
            >
              {totalCount}
            </Badge>
          </Flex>
        )}
        <StatCard
          to="/dashboard/offerAndHireProcess/offer_processing"
          data-testid="offersProcessingNavigateBtn"
          ml={totalCount > 0 ? '20px' : '0px'}
          icon={outstandingOffersIcon}
          label={t('toDoList:outstandingOffers')}
          count={counts.outsandingOffers}
          hoverBorderColor="#1F3CBA"
        />
        <StatCard
          to="/dashboard/offerAndHireProcess/hire_processing"
          data-testid="hiresProcessingNavigateBtn"
          ml="20px"
          icon={hiresProcessingIcon}
          label={t('toDoList:hiresProcessing')}
          count={counts.hireProcessing}
        />

        {isRequisitionEnabled && (
          <StatCard
            to="/dashboard/requisition"
            data-testid="requisitionNavigateBtn"
            ml="20px"
            icon={requisitionIcon}
            label={t('requisition:requisitionRiskDashboard')}
          />
        )}

        <StatCard noLink ml="20px" icon={toDoIcon} label={t('toDoList:toDo')} count={totalTodo} />
      </Flex>
    </Box>
  );
});
